import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion, useMotionTemplate, useMotionValue, AnimatePresence } from 'framer-motion';
import { Zap } from 'lucide-react';

// ✅ Eager loaded — needed immediately (above the fold)
import Header from './components/Header';
import Hero from './components/Hero';
import CustomCursor from './components/CustomCursor';
import ScrollToTop from './components/ScrollToTop';

// ✅ Lazy loaded — below the fold or only shown on specific views
const AboutSection = lazy(() => import('./components/AboutSection'));
const FeaturesSection = lazy(() => import('./components/FeaturesSection'));
const EfficiencySection = lazy(() => import('./components/EfficiencySection'));
const SupportSection = lazy(() => import('./components/SupportSection'));
const DesignSection = lazy(() => import('./components/DesignSection'));
const LaunchSection = lazy(() => import('./components/LaunchSection'));
const IntegrationsSection = lazy(() => import('./components/IntegrationsSection'));
const ComparisonSection = lazy(() => import('./components/ComparisonSection'));
const TestimonialSection = lazy(() => import('./components/TestimonialSection'));
const FAQSection = lazy(() => import('./components/FAQSection'));
const CTASection = lazy(() => import('./components/CTASection'));
const Footer = lazy(() => import('./components/Footer'));
const RevolutionSection = lazy(() => import('./components/RevolutionSection'));
const ContactFormSection = lazy(() => import('./components/ContactFormSection'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/TermsConditions'));
const ServicePage = lazy(() => import('./components/ServicePage'));
const BlogPage = lazy(() => import('./components/BlogPage'));

// Minimal fallback — invisible, no layout shift
const PageFallback = () => <div style={{ minHeight: '100px' }} />;

const App: React.FC = () => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Production Flag
  const isProd = true;

  // Preloading State
  const [isPreloading, setIsPreloading] = useState(true);

  // Theme State
  const [isDark, setIsDark] = useState(true);

  const getInitialView = (): 'home' | 'about' | 'service' | 'contact' | 'privacy' | 'terms' | 'blog' => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.replace(/\/$/, '');
      if (path === '/contact') return 'contact';
      if (path === '/about') return 'about';
      if (path === '/service' || path === '/services') return 'service';
      // if (path === '/blog' || path === '/blogs') return 'blog';
      if (path === '/privacy') return 'privacy';
      if (path === '/terms') return 'terms';

      const hash = window.location.hash;
      if (hash === '#contact') return 'contact';
      if (hash === '#about') return 'about';
      if (hash === '#service' || hash === '#services') return 'service';
      if (hash === '#privacy') return 'privacy';
      if (hash === '#terms') return 'terms';
    }
    return 'home';
  };

  // View State
  const [currentView, setCurrentView] = useState<'home' | 'about' | 'service' | 'contact' | 'privacy' | 'terms' | 'blog'>(getInitialView());

  // Ripple State
  const [ripple, setRipple] = useState<{ x: number; y: number; active: boolean }>({ x: 0, y: 0, active: false });

  // Fade State
  const [isFading, setIsFading] = useState(false);

  // Slide State
  const [isSliding, setIsSliding] = useState(false);

  // Unfold State
  const [isUnfolding, setIsUnfolding] = useState(false);

  // History Management
  useEffect(() => {
    if (isProd) {
      if (!window.history.state) {
        window.history.replaceState({ view: 'home' }, '', window.location.pathname);
      }

      const handlePopState = (event: PopStateEvent) => {
        if (event.state && event.state.view) {
          setCurrentView(event.state.view);
          window.scrollTo({ top: 0, behavior: 'instant' });
        } else {
          setCurrentView('home');
          window.scrollTo({ top: 0, behavior: 'instant' });
        }
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [isProd]);

  const updateHistory = (view: string) => {
    const url = view === 'home' ? '/' : `/${view}`;
    if (view === currentView) return;
    if (isProd) {
      if (view === 'home') {
        window.history.replaceState({ view: 'home' }, '', url);
      } else {
        if (currentView === 'home') {
          window.history.pushState({ view }, '', url);
        } else {
          window.history.replaceState({ view }, '', url);
        }
      }
    }
  };

  // Handle Initial Loading
  useEffect(() => {
    if (isPreloading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    const handleLoad = () => {
      setTimeout(() => {
        setIsPreloading(false);
      }, 1500);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, [isPreloading]);

  // Initialize theme
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Update Document Title based on View
  useEffect(() => {
    const titles = {
      home: 'Neotronics AI - AI Driven IT Consulting',
      about: 'About Us - Neotronics AI',
      service: 'Service Integration - Neotronics AI',
      contact: 'Contact Us - Neotronics AI',
      privacy: 'Privacy Policy - Neotronics AI',
      terms: 'Terms & Conditions - Neotronics AI',
      // blog: 'Blog - Neotronics AI'
    };
    document.title = titles[currentView as keyof typeof titles] || 'Neotronics AI';
  }, [currentView]);

  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  const handleNavClick = (name: string, href: string, e?: React.MouseEvent) => {
    let targetView: 'home' | 'about' | 'service' | 'contact' | 'privacy' | 'terms' | 'blog' = 'home';

    if (name === 'About') targetView = 'about';
    else if (name === 'Service') targetView = 'service';
    else if (name === 'Privacy') targetView = 'privacy';
    else if (name === 'Terms') targetView = 'terms';
    else if (name === 'Contact') targetView = 'contact';
    // else if (name === 'Blogs') targetView = 'blog';
    else targetView = 'home';

    if (name === 'Service' || name === 'Privacy' || name === 'Terms' || name === 'Blogs') {
      const x = e ? e.clientX : window.innerWidth / 2;
      const y = e ? e.clientY : window.innerHeight / 2;

      setRipple({ x, y, active: true });

      setTimeout(() => {
        setCurrentView(targetView);
        updateHistory(targetView);
        window.scrollTo({ top: 0, behavior: 'instant' });

        setTimeout(() => {
          setRipple(prev => ({ ...prev, active: false }));
        }, 50);
      }, 200);

    } else if (name === 'About') {
      setIsFading(true);

      setTimeout(() => {
        setCurrentView(targetView);
        updateHistory(targetView);
        window.scrollTo({ top: 0, behavior: 'instant' });
        setIsFading(false);

        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }, 500);

    } else if (name === 'Contact') {
      setIsSliding(true);

      setTimeout(() => {
        setCurrentView('contact');
        updateHistory('contact');
        window.scrollTo({ top: 0, behavior: 'instant' });
        setIsSliding(false);

        setTimeout(() => {
          const element = document.getElementById('contact-form');
          if (element) {
            element.scrollIntoView({ behavior: 'instant' });
          } else {
            window.scrollTo({ top: 0, behavior: 'instant' });
          }
        }, 50);
      }, 500);

    } else {
      setIsUnfolding(true);

      setTimeout(() => {
        setCurrentView('home');
        updateHistory('home');
        window.scrollTo({ top: 0, behavior: 'instant' });
        setIsUnfolding(false);

        setTimeout(() => {
          if (href === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (href.startsWith('#')) {
            const id = href.substring(1);
            const element = document.getElementById(id);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }, 100);
      }, 500);
    }
  };

  const handleBookAppointment = () => {
    handleNavClick('Contact', '/contact');
  };

  function handleMouseMove({ clientX, clientY }: React.MouseEvent) {
    mouseX.set(clientX);
    mouseY.set(clientY);
  }

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white font-sans selection:bg-brand-red selection:text-white overflow-x-hidden group transition-colors duration-300 md:cursor-none"
      onMouseMove={handleMouseMove}
    >
      {/* Initial Load Ripple Preloader */}
      <AnimatePresence>
        {isPreloading && (
          <motion.div
            key="initial-preloader"
            initial={{ opacity: 1, clipPath: 'circle(150% at 50% 50%)' }}
            exit={{
              clipPath: 'circle(0% at 50% 50%)',
              transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] }
            }}
            className="fixed inset-0 z-[200] bg-brand-red flex flex-col items-center justify-center pointer-events-auto"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{
                scale: [1, 1.15, 1],
                opacity: 1
              }}
              transition={{
                scale: { repeat: Infinity, duration: 2, ease: "easeInOut" },
                opacity: { duration: 0.5 }
              }}
              className="flex flex-col items-center gap-6"
            >
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-[0_0_40px_rgba(255,255,255,0.3)]">
                <Zap className="w-10 h-10 text-brand-red fill-brand-red" />
              </div>
              <motion.div
                animate={{ opacity: [0.4, 1, 0.4] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="text-white text-sm font-bold tracking-[0.3em] uppercase"
              >
                Neotronics
              </motion.div>
            </motion.div>

            <motion.div
              animate={{ scale: [1, 1.5], opacity: [0.3, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: "easeOut" }}
              className="absolute w-[300px] h-[300px] rounded-full border border-white/20"
            />
            <motion.div
              animate={{ scale: [1, 1.8], opacity: [0.2, 0] }}
              transition={{ repeat: Infinity, duration: 2, delay: 0.5, ease: "easeOut" }}
              className="absolute w-[300px] h-[300px] rounded-full border border-white/10"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ripple Transition Overlay */}
      <AnimatePresence>
        {ripple.active && (
          <motion.div
            initial={{ clipPath: `circle(0px at ${ripple.x}px ${ripple.y}px)` }}
            animate={{ clipPath: `circle(150% at ${ripple.x}px ${ripple.y}px)` }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-[100] bg-brand-red pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Fade Transition Overlay */}
      <AnimatePresence>
        {isFading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[100] bg-gray-50 dark:bg-black"
          />
        )}
      </AnimatePresence>

      {/* Slide Transition Overlay */}
      <AnimatePresence>
        {isSliding && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: '0%' }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[100] bg-brand-red"
          />
        )}
      </AnimatePresence>

      {/* Unfold Transition Overlay */}
      <AnimatePresence>
        {isUnfolding && (
          <div className="fixed inset-0 z-[100] flex flex-col pointer-events-none">
            <motion.div
              initial={{ y: "-100%" }}
              animate={{ y: "0%" }}
              exit={{ y: "-100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="flex-1 w-full bg-black border-b border-white/10 pointer-events-auto"
            />
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: "0%" }}
              exit={{ y: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="flex-1 w-full bg-black border-t border-white/10 pointer-events-auto"
            />
          </div>
        )}
      </AnimatePresence>

      {/* Global Interactive Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] opacity-20" />

        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 hidden md:block"
          style={{
            background: useMotionTemplate`
              radial-gradient(
                600px circle at ${mouseX}px ${mouseY}px,
                rgba(255, 59, 59, 0.15),
                transparent 80%
              )
            `
          }}
        />

        <div
          className="absolute inset-0 block md:hidden opacity-80"
          style={{
            background: `radial-gradient(
                600px circle at center,
                rgba(255, 59, 59, 0.15),
                transparent 80%
              )`
          }}
        />
      </div>

      {/* High Fidelity Custom Cursor */}
      <CustomCursor />

      {/* Content Wrapper */}
      <div className="relative z-10">
        <Header
          isDark={isDark}
          toggleTheme={toggleTheme}
          onNavClick={handleNavClick}
          currentView={currentView}
        />

        <main>
          <Suspense fallback={<PageFallback />}>
            {currentView === 'privacy' && <PrivacyPolicy />}

            {currentView === 'terms' && <TermsConditions />}

            {/* {currentView === 'blog' && <BlogPage />} */}

            {currentView === 'service' && (
              <ServicePage onBookAppointment={handleBookAppointment} />
            )}

            {currentView === 'about' && (
              <>
                <RevolutionSection />
                <DesignSection onBookAppointment={handleBookAppointment} />
                <FAQSection />
                <CTASection onBookAppointment={handleBookAppointment} />
              </>
            )}

            {currentView === 'contact' && (
              <>
                <ContactFormSection />
                <FAQSection />
                <CTASection onBookAppointment={handleBookAppointment} />
              </>
            )}

            {currentView === 'home' && (
              <>
                <Hero
                  onConnectClick={() => handleNavClick('Contact', '/contact')}
                  onWhoWeAreClick={(e) => handleNavClick('About', '/about', e)}
                />
                <AboutSection onBookAppointment={handleBookAppointment} />
                <FeaturesSection onBookAppointment={handleBookAppointment} />
                <EfficiencySection />
                <SupportSection onContactClick={() => handleNavClick('Contact', '/contact')} />
                <DesignSection onBookAppointment={handleBookAppointment} />
                <LaunchSection onBookAppointment={handleBookAppointment} />
                <IntegrationsSection onViewAboutClick={(e) => handleNavClick('About', '/about', e)} />
                {/* <ComparisonSection /> */}
                {/* <TestimonialSection onBookAppointment={handleBookAppointment} /> */}
                <CTASection onBookAppointment={handleBookAppointment} />
              </>
            )}
          </Suspense>
        </main>

        <Suspense fallback={null}>
          <Footer onNavClick={handleNavClick} />
        </Suspense>
      </div>

      <ScrollToTop />
    </div>
  );
};

export default App;